import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { addNotification } from '../../store/slices/uiSlice';
import { API_URL } from '../../config/api';

const AdminRunScriptPanel = () => {
  const dispatch = useDispatch();
  const { token } = useSelector((state) => state.auth);
  const [scriptRunning, setScriptRunning] = useState(false);
  const [scriptResult, setScriptResult] = useState(null);

  const [hiddenRunning, setHiddenRunning] = useState(false);
  const [hiddenResult, setHiddenResult] = useState(null);

  const [emptyRunning, setEmptyRunning] = useState(false);
  const [emptyResult, setEmptyResult] = useState(null);

  const handleMapWorkflowAndTask = async () => {
    if (!window.confirm('Map journey tasks for existing workflows that are missing the new task structure? This can be run more than once.')) {
      return;
    }
    try {
      setScriptRunning(true);
      const response = await axios.post(
        `${API_URL}/admin/scripts/map-workflow-tasks`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setScriptResult(response.data || null);
      dispatch(addNotification({
        type: 'success',
        message: response.data?.message || 'Workflow and task mapping complete',
      }));
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: error.response?.data?.error || 'Failed to map workflow and tasks',
      }));
    } finally {
      setScriptRunning(false);
    }
  };

  const handleCompleteHiddenWorkflowTasks = async () => {
    if (!window.confirm('Complete hidden journey tasks on open workflows? These are auto-created tasks that are not shown on screen but still block Close workflow. Explicit +task items are left unchanged. Safe to run more than once.')) {
      return;
    }
    try {
      setHiddenRunning(true);
      const response = await axios.post(
        `${API_URL}/admin/scripts/complete-hidden-workflow-tasks`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHiddenResult(response.data || null);
      dispatch(addNotification({
        type: 'success',
        message: response.data?.message || 'Hidden workflow tasks completed',
      }));
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: error.response?.data?.error || 'Failed to complete hidden workflow tasks',
      }));
    } finally {
      setHiddenRunning(false);
    }
  };

  const handleCompleteEmptyWorkflowTasks = async () => {
    if (!window.confirm('For open workflows that have no linked task, complete leftover untitled shells or create a completed journey task so Close workflow is not blocked. Workflows that already have a titled task are left unchanged. Safe to run more than once.')) {
      return;
    }
    try {
      setEmptyRunning(true);
      const response = await axios.post(
        `${API_URL}/admin/scripts/complete-empty-workflow-tasks`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEmptyResult(response.data || null);
      dispatch(addNotification({
        type: 'success',
        message: response.data?.message || 'Empty workflow tasks completed',
      }));
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: error.response?.data?.error || 'Failed to complete empty workflow tasks',
      }));
    } finally {
      setEmptyRunning(false);
    }
  };

  const anyRunning = scriptRunning || hiddenRunning || emptyRunning;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
        Run script
      </h2>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
        One-off maintenance for workflows and the tasks linked to them.
      </p>
      <div className="space-y-8">
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Check existing workflows that were raised before journey tasks were created, then map a task for each one. Safe to run more than once.
          </p>
          <button
            type="button"
            onClick={handleMapWorkflowAndTask}
            disabled={anyRunning}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {scriptRunning ? 'Mapping…' : 'Map Workflow and task'}
          </button>
          {scriptResult && (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-3">
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Scanned</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{scriptResult.scanned ?? 0}</p>
              </div>
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Created</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{scriptResult.created ?? 0}</p>
              </div>
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Upgraded</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{scriptResult.upgraded ?? 0}</p>
              </div>
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Already mapped</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{scriptResult.skipped ?? 0}</p>
              </div>
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Template linked</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{scriptResult.linkedTemplate ?? 0}</p>
              </div>
            </div>
          )}
        </div>

        <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Complete hidden auto-created journey tasks on open workflows. Those tasks are not shown on screen but still block Close workflow. Tasks users added with +task are not changed.
          </p>
          <button
            type="button"
            onClick={handleCompleteHiddenWorkflowTasks}
            disabled={anyRunning}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {hiddenRunning ? 'Completing…' : 'Complete hidden workflow tasks'}
          </button>
          {hiddenResult && (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Scanned</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{hiddenResult.scanned ?? 0}</p>
              </div>
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Workflows fixed</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{hiddenResult.workflowsTouched ?? 0}</p>
              </div>
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Tasks completed</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{hiddenResult.completed ?? 0}</p>
              </div>
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Skipped</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{hiddenResult.skipped ?? 0}</p>
              </div>
            </div>
          )}
        </div>

        <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Open workflows with no task inside cannot be closed. This completes leftover untitled shells, or creates a completed journey task when none exist. Workflows that already have a titled task are not changed.
          </p>
          <button
            type="button"
            onClick={handleCompleteEmptyWorkflowTasks}
            disabled={anyRunning}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {emptyRunning ? 'Completing…' : 'Complete empty workflow tasks'}
          </button>
          {emptyResult && (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-3">
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Scanned</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{emptyResult.scanned ?? 0}</p>
              </div>
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Workflows fixed</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{emptyResult.workflowsTouched ?? 0}</p>
              </div>
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Tasks created</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{emptyResult.created ?? 0}</p>
              </div>
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Shells completed</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{emptyResult.completed ?? 0}</p>
              </div>
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Skipped</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{emptyResult.skipped ?? 0}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminRunScriptPanel;
